import { CHAT_LANGUAGE_OPTIONS } from '../constants/chatLanguages'

const SUPPORTED_UI_LOCALE_LANGUAGES = Object.freeze(['en', 'de', 'ar', 'fi', 'he', 'hu', 'pl', 'ro'])
const selectableUiLanguageCodes = new Map(
    CHAT_LANGUAGE_OPTIONS.map(option => [String(option.code).toLowerCase(), option.code])
)
const uiLocaleCatalogCache = new Map([['en', Object.freeze({})]])
const uiLocaleCatalogPromises = new Map()
let uiLocaleSourceCatalogCache = null
let uiLocaleSourceCatalogPromise = null

const normalizeUiLocaleLanguage = value => {
    const language = String(value ?? 'en').trim()
    if (!language || language === 'original')
        return 'en'

    const selectableLanguage = selectableUiLanguageCodes.get(language.toLowerCase())
    if (selectableLanguage)
        return selectableLanguage

    return /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/.test(language) ? language : 'en'
}

const normalizeUiLocaleCatalogLanguage = value =>
    normalizeUiLocaleLanguage(value).toLowerCase()

const hasLocalUiLocaleCatalog = language =>
    SUPPORTED_UI_LOCALE_LANGUAGES.includes(normalizeUiLocaleCatalogLanguage(language))

const normalizeUiLocaleLookupText = value =>
    String(value ?? '')
        .replace(/\s+/g, ' ')
        .trim()

const getCachedUiLocaleCatalog = language =>
    uiLocaleCatalogCache.get(normalizeUiLocaleCatalogLanguage(language)) ?? {}

const normalizeLocalePayload = payload =>
    payload && typeof payload === 'object' && !Array.isArray(payload) ?
        Object.fromEntries(
            Object.entries(payload)
                .map(([key, value]) => [normalizeUiLocaleLookupText(key), String(value ?? '')])
                .filter(([key, value]) => key && value)
        ) :
        {}

const hasCachedUiLocaleTranslation = (language, text) => {
    const normalizedText = normalizeUiLocaleLookupText(text)
    if (!normalizedText)
        return false

    return Object.hasOwn(getCachedUiLocaleCatalog(language), normalizedText)
}

const readCachedUiLocaleTranslation = (language, text) => {
    const normalizedText = normalizeUiLocaleLookupText(text)
    if (!normalizedText)
        return ''

    return String(getCachedUiLocaleCatalog(language)?.[normalizedText] ?? '')
}

const loadUiLocaleCatalog = async language => {
    const normalizedLanguage = normalizeUiLocaleCatalogLanguage(language)

    if (normalizedLanguage === 'en' || !hasLocalUiLocaleCatalog(normalizedLanguage))
        return {}

    const cachedCatalog = uiLocaleCatalogCache.get(normalizedLanguage)
    if (cachedCatalog)
        return cachedCatalog

    const pendingPromise = uiLocaleCatalogPromises.get(normalizedLanguage)
    if (pendingPromise)
        return pendingPromise

    const nextPromise = fetch(`/ui-locales/${normalizedLanguage}.json`)
        .then(async response => {
            if (!response.ok)
                throw new Error(`UI locale request failed (${response.status})`)

            const payload = await response.json()
            const normalizedPayload = normalizeLocalePayload(payload)

            uiLocaleCatalogCache.set(normalizedLanguage, normalizedPayload)
            uiLocaleCatalogPromises.delete(normalizedLanguage)
            return normalizedPayload
        })
        .catch(error => {
            console.warn('[UiLocaleCatalog] Could not load locale catalog:', error)
            uiLocaleCatalogCache.set(normalizedLanguage, {})
            uiLocaleCatalogPromises.delete(normalizedLanguage)
            return {}
        })

    uiLocaleCatalogPromises.set(normalizedLanguage, nextPromise)
    return nextPromise
}

const getCachedUiLocaleSourceCatalog = () =>
    uiLocaleSourceCatalogCache ?? {}

const loadUiLocaleSourceCatalog = async () => {
    if (uiLocaleSourceCatalogCache)
        return uiLocaleSourceCatalogCache

    if (uiLocaleSourceCatalogPromise)
        return uiLocaleSourceCatalogPromise

    uiLocaleSourceCatalogPromise = fetch('/ui-locales/en.json')
        .then(async response => {
            if (!response.ok)
                throw new Error(`UI source locale request failed (${response.status})`)

            const payload = await response.json()
            uiLocaleSourceCatalogCache = normalizeLocalePayload(payload)
            uiLocaleSourceCatalogPromise = null
            return uiLocaleSourceCatalogCache
        })
        .catch(error => {
            console.warn('[UiLocaleCatalog] Could not load source locale catalog:', error)
            uiLocaleSourceCatalogCache = {}
            uiLocaleSourceCatalogPromise = null
            return uiLocaleSourceCatalogCache
        })

    return uiLocaleSourceCatalogPromise
}

export {
    SUPPORTED_UI_LOCALE_LANGUAGES,
    normalizeUiLocaleLanguage,
    normalizeUiLocaleLookupText,
    hasLocalUiLocaleCatalog,
    getCachedUiLocaleCatalog,
    getCachedUiLocaleSourceCatalog,
    hasCachedUiLocaleTranslation,
    readCachedUiLocaleTranslation,
    loadUiLocaleCatalog,
    loadUiLocaleSourceCatalog
}
